import { ChartInsight, isEarningsInsight, isInsightFilter } from '@api';
import { xAxis } from '@chart/core/axis/axis';
import {
  dispatchGutterSizeEvent,
  dispatchInsightsInDomainEvent,
} from '@chart/core/events/events';
import { Domain } from '@chart/types/axis.types';
import {
  BaseChartAPIProps,
  ChartPane,
  CreateChartOptionsWithColors,
} from '@chart/types/create.types';
import { ResampleIntervals } from '@chart/types/resample.types';
import {
  createChartPane,
  createChartPanes,
  getPaneHeights,
  getPaneTop,
  getYAxisSizes,
} from '@chart/utils/pane/pane-utils';
import { getThresholdIntervals } from '@chart/utils/threshold/threshold-utils';
import {
  createFakeTS,
  getChartDomain,
  MIN_IDX_RANGE,
} from '@chart/utils/timeseries/time-series';
import { getYAxisDomain } from '@chart/utils/y-scale/y-scale';
import { compareObjectKeys } from '@helpers';
import { ZoomBehavior } from 'd3';
import { scaleLinear } from 'd3-scale';
import { create } from 'zustand';

import { InitProps } from '../setup/setup';

interface InitialiseProps extends InitProps {
  prevBase?: BaseChartAPIProps;
  options: CreateChartOptionsWithColors;
  context: CanvasRenderingContext2D;
  canvasElement: HTMLCanvasElement;
}

export interface ChartStore {
  base?: BaseChartAPIProps;
  zoom?: {
    zoomBehavior: ZoomBehavior<HTMLCanvasElement, unknown>;
    hasDomainChanged?: boolean;
  };
  initialise: (props: InitialiseProps) => BaseChartAPIProps;
  updateDomain: (domain: Domain) => BaseChartAPIProps;
  setInsights: (insights: ChartInsight[]) => BaseChartAPIProps;
  resizePane: (paneIndex: number, diff: number) => void;
  updateLastPrice: (
    price: number,
    timestamp?: string,
    entityIdentifier?: string
  ) => void;
}

const calculateBase = ({
  prevBase,
  resampleInterval,
  horizon,
  context,
  options,
  canvasElement,
  domain: customDomain,
  panes,
  primaryAsset,
  primaryPaneId,
}: InitialiseProps): BaseChartAPIProps => {
  const ts = primaryAsset.ts;
  const lastDataIdx = ts.length - 1;

  const domain = getChartDomain({
    prevBase,
    lastDataIdx,
    horizon,
    resampleInterval,
    timeseries: ts,
    customDomain,
  });

  const extraTS =
    ts.length >= 2
      ? createFakeTS({
          ts: primaryAsset.ts,
          index1: ts.length - 1,
          index2: ts.length - 2,
          dataPointsToAdd: Math.round(ts.length / 2),
          lastKnownDate: new Date(ts[ts.length - 1].time),
        })
      : [];

  const yAxisDomain = getYAxisDomain(domain);

  const maximizedPane = panes.find(p => p.maximized);
  const activePanes = maximizedPane ? [maximizedPane] : panes;
  const paneHeights = getPaneHeights({ options, panes: activePanes, prevBase });
  const chartPanes = createChartPanes({
    prevBase,
    panes: activePanes,
    paneHeights,
    options,
    yAxisDomain,
    primaryAsset,
  });

  const prevPanes = chartPanes[0].maximized
    ? prevBase?.prevPanes?.map(p =>
        p.id === chartPanes[0].id ? chartPanes[0] : p
      )
    : chartPanes;
  const { yAxisSizes, totalWidth } = getYAxisSizes(chartPanes);

  const optionsUpdated: CreateChartOptionsWithColors = {
    ...options,
    gutters: {
      x: options.gutters.x,
      y: totalWidth,
    },
  };

  const tsWithFake = [...primaryAsset.ts, ...extraTS];
  const isPannedInitial = domain[1] < lastDataIdx;
  const x = xAxis({
    panned: isPannedInitial,
    options: optionsUpdated,
    timeseries: tsWithFake,
    domain,
    resampleInterval,
  });

  const thresholdIntervals = getThresholdIntervals({
    domain: domain,
    chartPanes,
    primaryAsset,
    x,
  });

  const lastIdx = lastDataIdx + extraTS.length - 1;
  const fullXScale = scaleLinear()
    .domain([0, lastIdx])
    .range([0, optionsUpdated.width - optionsUpdated.gutters.y]);
  const maxChartZoom = lastDataIdx / MIN_IDX_RANGE;

  const baseProps: BaseChartAPIProps = {
    ...prevBase,
    primaryAsset,
    options: optionsUpdated,
    resampleInterval,
    horizon,
    context,
    fullXScale,
    lastDataIdx,
    maxChartZoom,
    canvasElement,
    domain,
    x,
    panes: chartPanes,
    thresholdIntervals,
    prevPanes,
    yAxisSizes,
    primaryPaneId,
    timeseries: tsWithFake,
    panned: isPannedInitial,
  };
  return baseProps;
};

const normalizeDate = (
  dateStr: string,
  interval: ResampleIntervals
): string => {
  const date = new Date(dateStr);
  const d = new Date(date);

  switch (interval) {
    case ResampleIntervals.OneMinute:
      d.setUTCSeconds(0, 0);
      break;
    case ResampleIntervals.FiveMinutes:
      d.setUTCMinutes(Math.floor(d.getUTCMinutes() / 5) * 5, 0, 0);
      break;
    case ResampleIntervals.FifteenMinutes:
      d.setUTCMinutes(Math.floor(d.getUTCMinutes() / 15) * 15, 0, 0);
      break;
    case ResampleIntervals.ThirtyMinutes:
      d.setUTCMinutes(Math.floor(d.getUTCMinutes() / 30) * 30, 0, 0);
      break;
    case ResampleIntervals.OneHour:
      d.setUTCMinutes(0, 0, 0);
      break;
    case ResampleIntervals.OneDay:
      d.setUTCHours(0, 0, 0, 0);
      break;

    case ResampleIntervals.OneWeek: {
      const day = d.getUTCDay();
      const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
      d.setUTCDate(diff);
      d.setUTCHours(0, 0, 0, 0);
      break;
    }

    case ResampleIntervals.OneMonth:
      d.setUTCDate(1);
      d.setUTCHours(0, 0, 0, 0);
      break;
  }

  // Return without milliseconds, e.g. '2025-12-11T16:34:00Z' to sync with BE ts time
  const iso = d.toISOString();
  return `${iso.slice(0, 19)}Z`;
};

const chartStoresMap = new Map<HTMLCanvasElement, ChartStoreReturn>();
export type ChartStoreReturn = ReturnType<typeof createChartStore>;

export const createChartStore = () =>
  create<ChartStore>((set, get) => ({
    base: undefined,

    initialise: props => {
      const prevBase = get().base;
      const newBase = calculateBase({
        prevBase,
        ...props,
      });

      dispatchGutterSizeEvent(newBase, prevBase);
      set({ base: newBase });
      return newBase;
    },
    updateDomain: domain => {
      const base = get().base as BaseChartAPIProps;
      const options = base.options;
      const lastRealIdx = base.primaryAsset.ts.length - 1;

      // this domain will be used to display line,
      // so it should always use indexes from real timeseries
      const newDomain: Domain = [
        domain[0],
        domain[1] > lastRealIdx ? lastRealIdx : domain[1],
      ];
      const yAxisDomain = getYAxisDomain(newDomain);
      const prevYAxisDomain = getYAxisDomain(base.domain);

      const paneHeights = base.panes.map(p => p.options.height);
      const newPanes = compareObjectKeys(prevYAxisDomain, yAxisDomain, [
        '0',
        '1',
      ])
        ? base.panes
        : createChartPanes({
            prevBase: base,
            panes: base.panes,
            paneHeights,
            options: options,
            yAxisDomain,
            primaryAsset: base.primaryAsset,
          });

      const { yAxisSizes, totalWidth } = getYAxisSizes(newPanes);

      const updatedOptions: CreateChartOptionsWithColors = {
        ...options,
        gutters: {
          x: options.gutters.x,
          y: totalWidth,
        },
      };
      const isPanned = newDomain[1] < lastRealIdx;
      const x = xAxis({
        ...base,
        panned: isPanned,
        options: updatedOptions,
        timeseries: base.timeseries,
        domain,
      });

      const thresholdIntervals = getThresholdIntervals({
        domain: newDomain,
        chartPanes: newPanes,
        primaryAsset: base.primaryAsset,
        x,
      });

      const newBase = {
        ...base,
        options: updatedOptions,
        domain: newDomain,
        x,
        panes: newPanes,
        thresholdIntervals,
        yAxisSizes,
        panned: isPanned,
      };

      dispatchGutterSizeEvent(newBase, base);

      set({ base: newBase });
      return newBase;
    },
    setInsights: insights => {
      const base = get().base as BaseChartAPIProps;

      const entityToFirstPaneIndex = new Map<string, number>();
      base.panes.forEach((pane, paneIndex) => {
        pane.chartAssetData.forEach(assetData => {
          const entityTag = assetData.entity.tag;

          if (!entityToFirstPaneIndex.has(entityTag)) {
            entityToFirstPaneIndex.set(entityTag, paneIndex);
          }
        });
      });

      const newPanes = base.panes.map((p, paneIndex) => ({
        ...p,
        insights: insights.filter(i => {
          let tag: string;

          if (isInsightFilter(i)) {
            tag = i.metadata.entity;
          } else if (isEarningsInsight(i)) {
            tag = i.entity_tag;
          } else {
            tag = (i as { identifier: string }).identifier;
          }

          const firstPaneIndex = entityToFirstPaneIndex.get(tag);
          return (
            firstPaneIndex === paneIndex &&
            p.chartAssetData.some(c => c.entity.tag === tag)
          );
        }),
      }));
      const newBase: BaseChartAPIProps = {
        ...base,
        panes: newPanes,
        prevPanes: newPanes,
      };
      set({ base: newBase });
      return newBase;
    },
    resizePane: (paneDividerIndex, diff) => {
      const base = get().base as BaseChartAPIProps;
      const collapsedPaneHeight = base.options.config.collapsedPaneHeight;
      const chartPanes: ChartPane[] = [];
      const indexesToUpdate = [paneDividerIndex - 1, paneDividerIndex];

      base.panes.forEach((pane, index) => {
        if (indexesToUpdate.includes(index)) {
          const sign = index === paneDividerIndex ? -1 : 1;
          const change = diff * sign;
          const top =
            index === paneDividerIndex
              ? getPaneTop({ chartPanes, index })
              : pane.options.gutters.top;
          const height = Math.max(
            collapsedPaneHeight,
            pane.options.height + change
          );
          const createdPane = createChartPane({
            top,
            height,
            paneData: pane,
            options: base.options,
            insights: pane.insights,
            domainTimeSeries: pane.domainTimeSeries,
            primaryAsset: base.primaryAsset,
            actualHeight: height,
          });
          chartPanes.push(createdPane);
        } else {
          chartPanes.push(pane);
        }
      });

      const newBase = { ...base, panes: chartPanes, prevPanes: chartPanes };
      dispatchGutterSizeEvent(newBase, base);
      dispatchInsightsInDomainEvent(newBase);
      set({ base: newBase });
    },
    updateLastPrice: (price, timestamp, entityIdentifier) => {
      const base = get().base;

      if (!base) {
        return;
      }

      // Find the primary pane
      const primaryPaneIndex = base.panes.findIndex(
        p => p.id === base.primaryPaneId
      );

      if (primaryPaneIndex === -1) {
        return;
      }

      const primaryPane = base.panes[primaryPaneIndex];

      // Find the asset to update - if entityIdentifier is provided, find matching asset
      // Otherwise, use the first asset (backward compatible)
      const assetIndex = entityIdentifier
        ? Math.max(
            0,
            primaryPane.chartAssetData.findIndex(
              asset => asset.entity.default_snake === entityIdentifier
            )
          )
        : 0;

      const primaryAssetData = primaryPane.chartAssetData[assetIndex];

      if (!primaryAssetData || primaryAssetData.ts.length === 0) {
        return;
      }

      const lastBar = primaryAssetData.ts[primaryAssetData.ts.length - 1];

      // Normalize timestamp
      const normalizedTimestamp = timestamp
        ? normalizeDate(timestamp, base.resampleInterval)
        : undefined;

      // Create new updated structures
      let newPanes = [...base.panes];
      const newPrimaryPane = { ...primaryPane };
      const newChartAssetData = [...primaryPane.chartAssetData];
      const newPrimaryAssetData = { ...primaryAssetData };

      // Check if we should create a new candle or update the existing one
      if (normalizedTimestamp && normalizedTimestamp !== lastBar.time) {
        // Create a new candle
        const newCandle = {
          time: normalizedTimestamp,
          open: lastBar.close,
          high: Math.max(lastBar.close, price),
          low: Math.min(lastBar.close, price),
          close: price,
        };

        // Create new ts array with the new candle
        newPrimaryAssetData.ts = [...primaryAssetData.ts, newCandle];
        newPrimaryAssetData.tsByTime = new Map(primaryAssetData.tsByTime);
        newPrimaryAssetData.tsByTime.set(
          normalizedTimestamp,
          newPrimaryAssetData.ts.length - 1
        );
      } else {
        // Update the existing candle
        const updatedLastBar = {
          ...lastBar,
          close: price,
          high: Math.max(lastBar.open, price, lastBar.high),
          low: Math.min(lastBar.open, price, lastBar.low),
        };

        // Create new ts array with updated last candle
        newPrimaryAssetData.ts = [
          ...primaryAssetData.ts.slice(0, -1),
          updatedLastBar,
        ];
      }

      // Update the chain of references
      newChartAssetData[assetIndex] = newPrimaryAssetData;
      newPrimaryPane.chartAssetData = newChartAssetData;
      newPanes[primaryPaneIndex] = newPrimaryPane;

      // Check if we're updating the actual primary asset (the one with longest history)
      const isUpdatingPrimaryAsset =
        primaryAssetData.entity.id === base.primaryAsset.entity.id;

      const isNewCandle =
        normalizedTimestamp && normalizedTimestamp !== lastBar.time;

      let domain = base.domain;
      let lastDataIdx = base.lastDataIdx;
      let timeseries = base.timeseries;
      let fullXScale = base.fullXScale;
      let maxChartZoom = base.maxChartZoom;
      let x = base.x;
      let options = base.options;

      if (isUpdatingPrimaryAsset && isNewCandle) {
        lastDataIdx = newPrimaryAssetData.ts.length - 1;

        // shift domain only if pinned to the edge
        if (base.domain[1] >= base.lastDataIdx) {
          domain = [base.domain[0] + 1, lastDataIdx];
        }

        const extraTS =
          newPrimaryAssetData.ts.length >= 2
            ? createFakeTS({
                ts: newPrimaryAssetData.ts,
                index1: newPrimaryAssetData.ts.length - 1,
                index2: newPrimaryAssetData.ts.length - 2,
                dataPointsToAdd: Math.round(newPrimaryAssetData.ts.length / 2),
                lastKnownDate: new Date(
                  newPrimaryAssetData.ts[newPrimaryAssetData.ts.length - 1].time
                ),
              })
            : [];

        timeseries = [...newPrimaryAssetData.ts, ...extraTS];
        const lastIdx = lastDataIdx + extraTS.length - 1;

        options = {
          ...base.options,
          gutters: {
            x: base.options.gutters.x,
            y: base.yAxisSizes.reduce((acc, curr) => acc + curr.width, 0),
          },
        };

        fullXScale = scaleLinear()
          .domain([0, lastIdx])
          .range([0, options.width - options.gutters.y]);

        maxChartZoom = lastDataIdx / MIN_IDX_RANGE;

        const isPannedNow = domain[1] < lastDataIdx;
        x = xAxis({
          ...base,
          panned: isPannedNow,
          timeseries,
          domain,
          options,
        });

        const yAxisDomain = getYAxisDomain(domain);
        const paneHeights = base.panes.map(p => p.options.height);
        const recomputedPanes = createChartPanes({
          prevBase: base,
          panes: newPanes,
          paneHeights,
          options,
          yAxisDomain,
          primaryAsset: {
            ...base.primaryAsset,
            ts: newPrimaryAssetData.ts,
            tsByTime: newPrimaryAssetData.tsByTime,
          },
        });

        const { yAxisSizes, totalWidth } = getYAxisSizes(recomputedPanes);

        options = {
          ...options,
          gutters: {
            x: options.gutters.x,
            y: totalWidth,
          },
        };
        x = xAxis({
          ...base,
          panned: isPannedNow,
          timeseries,
          domain,
          options,
        });

        newPanes = recomputedPanes;
        base.yAxisSizes = yAxisSizes;
      } else if (!isUpdatingPrimaryAsset) {
        // If updating a non-primary asset, we still need to recompute panes for y-axis updates
        // but don't change base.primaryAsset, base.timeseries, or x scale
        const yAxisDomain = getYAxisDomain(domain);
        const paneHeights = base.panes.map(p => p.options.height);
        const recomputedPanes = createChartPanes({
          prevBase: base,
          panes: newPanes,
          paneHeights,
          options,
          yAxisDomain,
          primaryAsset: base.primaryAsset,
        });

        const { yAxisSizes, totalWidth } = getYAxisSizes(recomputedPanes);

        options = {
          ...options,
          gutters: {
            x: options.gutters.x,
            y: totalWidth,
          },
        };

        newPanes = recomputedPanes;
        base.yAxisSizes = yAxisSizes;
      }

      set({
        base: {
          ...base,
          panes: newPanes,
          primaryAsset: isUpdatingPrimaryAsset
            ? {
                ...base.primaryAsset,
                ts: newPrimaryAssetData.ts,
                tsByTime: newPrimaryAssetData.tsByTime,
              }
            : base.primaryAsset,
          domain,
          lastDataIdx,
          timeseries,
          fullXScale,
          maxChartZoom,
          x,
          options,
          panned: domain[1] < lastDataIdx,
        },
      });
    },
  }));

export const initChartStore = (canvasElement: HTMLCanvasElement) => {
  const chartStore = chartStoresMap.get(canvasElement) || createChartStore();

  if (!chartStoresMap.has(canvasElement)) {
    chartStoresMap.set(canvasElement, chartStore);
  }

  return chartStore;
};
